"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  BookOpen,
  Sparkles,
  Crown,
  ArrowRight,
  Mic,
  Layers,
  Network,
  Compass,
  Building2,
  Target,
  Brain,
  Zap,
  Globe2,
  Heart,
  ShieldCheck,
  Lock,
  Shield,
  Activity,
  Eye,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";
import { PillarGridBlock } from "@/lib/blocks/pillar-grid-block";
import { FeatureGridBlock } from "@/lib/blocks/feature-grid-block";
import { AccordionBlock } from "@/lib/blocks/accordion-block";

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
};

export default function StrategicSovereigntyPage() {
  return (
    <>
      <PageHero
        icon={BookOpen}
        overline="Glossary · Strategic Sovereignty"
        overlineClassName="text-red"
        title={
          <>
            The Integrated Discipline of <span className="text-gradient-premium">Commanding Sovereign Outcomes</span>
          </>
        }
        subtitle="The capacity of an organization to exercise complete, independent control over intelligence, security, political strategy, reputation management, and governance execution — without reliance on third-party dependencies, external platforms, or fragmented advisory arrangements."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Glossary", href: "/glossary/" }, { label: "Strategic Sovereignty" }]}
        badge="Featured term · Penta P's framework"
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.red.pill} mb-5`}>
            <Sparkles className="size-3.5 text-red" />
            <span className="text-overline text-red">Formal Definition</span>
          </div>
          <h2 className="text-display-md mb-5">What Strategic Sovereignty is.</h2>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            <span className="font-semibold text-foreground">Strategic Sovereignty</span> is the capacity of an organization or operator to exercise complete, independent control over intelligence, security, political strategy, reputation management, and governance execution — without reliance on third-party dependencies, external platforms, or fragmented advisory arrangements.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            It is the operational state in which every variable is managed internally, every risk is mitigated through proprietary systems, and every outcome is verified against sovereign-grade standards. The entity that achieves Strategic Sovereignty does not outsource critical functions. It commands them.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed">
            The word "sovereignty" carries weight. In geopolitical terms, sovereignty means supreme authority within a territory. In operational terms, Strategic Sovereignty means supreme authority over every domain of execution — the intelligence that informs decisions, the privacy that protects them, the politics that shape them, the policing that secures them, and the policy that institutionalizes them.
          </p>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Layers className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">{`The Penta P's Framework`}</span>
          </div>
          <h2 className="text-display-md mb-4">Five interconnected pillars.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Strategic Sovereignty rests on five interconnected domains, each indispensable, none operating in isolation. Together, they form the Penta P's Framework.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { title: "Perception", body: "Reputation and narrative control. Managing public sentiment, crisis response, media strategy, and digital reputation at national scale.", accent: "red" },
            { title: "Privacy", body: "Security and encryption. Military-grade encryption, secure communications, counter-surveillance, and data sovereignty.", accent: "purple" },
            { title: "Politics", body: "Campaigns and governance. Electoral strategy, voter intelligence, political catalysis, and governance support.", accent: "cyan" },
            { title: "Policing", body: "Intelligence and defense. Threat detection, intelligence gathering, defense coordination, and national security operations.", accent: "emerald" },
            { title: "Policy", body: "Transformation and reform. Governance reform, institutional transformation, and smart policy execution.", accent: "amber-gold" },
            { title: "Integration", body: "The five pillars share data, intelligence, and learnings across the entire stack — a closed-loop system. No pillar functions effectively without the others.", accent: "denim" },
          ].map((p, i) => {
            const a = ACCENT[p.accent];
            return (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border}`}
              >
                <h3 className={`text-heading-md mb-2 ${a.text}`}>{p.title}</h3>
                <p className="text-caption text-muted-foreground leading-relaxed">{p.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <Target className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">Operational Context</span>
          </div>
          <h2 className="text-display-md mb-4">When Strategic Sovereignty applies.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Strategic Sovereignty is the operative standard for environments where the cost of failure is national instability, political collapse, or irreversible reputational damage to sovereign entities.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {[
            { dim: "Stakes", body: "The clients are governments, royal families, defense agencies, political leaders, and Fortune-scale corporations. The margin for error is the stability of a nation's narrative." },
            { dim: "Scale", body: "Eighteen active countries across three continents. Two hundred-plus platforms. Millions of data points. Fifteen-plus languages. Capabilities deployed in thirty-plus nations." },
            { dim: "Duration", body: "Fifteen-plus years of continuous operations, through every global disruption. The endurance of the operating model is itself proof of the concept." },
            { dim: "Integration", body: "Nine proprietary platforms coordinated through a single neural command interface. Every team sees only what they need to see. Unified command sees everything." },
            { dim: "Confidentiality", body: "National security level confidentiality. Zero security incidents across fifteen-plus years. The operational floor, not the aspiration." },
            { dim: "Outcome", body: "Verified results — not advisory theory. Campaigns won, nations stabilized, institutions transformed. Fifteen victories in eighteen engagements." },
          ].map((p, i) => {
            const accentColors = ["red", "purple", "cyan", "emerald", "amber-gold", "denim"];
            const a = ACCENT[accentColors[i]];
            return (
              <motion.div
                key={p.dim}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border}`}
              >
                <h3 className={`text-heading-sm mb-2 ${a.text}`}>{p.dim}</h3>
                <p className="text-caption text-muted-foreground leading-relaxed">{p.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.emerald.pill} mb-4`}>
            <Network className="size-3.5 text-emerald" />
            <span className="text-overline text-emerald">Related Terms</span>
          </div>
          <h2 className="text-display-md mb-4">The conceptual map.</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { term: "Penta P's Framework", href: "/glossary/penta-ps-framework/", desc: "The five-pillar architecture that structures Strategic Sovereignty.", accent: "cyan" },
            { term: "Sovereign Operations Architecture", href: "/glossary/sovereign-operations-architecture/", desc: "The six-stage methodology that operationalizes Strategic Sovereignty.", accent: "emerald" },
            { term: "Digital Conglomerate", href: "/glossary/digital-conglomerate/", desc: "The organizational form that achieves Strategic Sovereignty.", accent: "purple" },
            { term: "Operational Excellence", href: "/glossary/operational-excellence/", desc: "The engineered system that produces verified outcomes.", accent: "amber-gold" },
            { term: "eGovernance", href: "/glossary/egovernance/", desc: "The policy pillar operationalized through digital infrastructure.", accent: "lime" },
            { term: "Crisis Transformation", href: "/glossary/crisis-transformation/", desc: "The perception-pillar discipline for converting emergencies to advantage.", accent: "coral" },
          ].map((t, i) => {
            const a = ACCENT[t.accent];
            return (
              <motion.div
                key={t.href}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
              >
                <Link href={t.href} className="block h-full group">
                  <div className={`relative glass-card rounded-2xl p-5 h-full bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}>
                    <h3 className={`text-heading-sm mb-2 ${a.text}`}>{t.term}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed mb-3">{t.desc}</p>
                    <span className={`inline-flex items-center gap-1.5 text-caption font-semibold ${a.text} group-hover:gap-2.5 transition-all`}>
                      Read <ArrowRight className="size-3" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-4`}>
            <Crown className="size-3.5 text-purple" />
            <span className="text-overline text-purple">Sovereignty Pillars</span>
          </div>
          <h2 className="text-display-md mb-4">The structural pillars of sovereignty.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Five structural pillars that hold the sovereign operational state in place. Each pillar is a capacity that must be built, maintained, and continuously refined — not a value declared, but a system engineered.
          </p>
        </div>
        <PillarGridBlock
          pillars={[
            { title: "Intelligence Sovereignty", body: "The capacity to gather, process, and act on intelligence without dependence on external providers. Sovereign intelligence capabilities eliminate information asymmetries that adversaries exploit.", accent: "cyan" },
            { title: "Operational Sovereignty", body: "The capacity to execute operations without third-party dependencies. Every system, protocol, and platform is built and maintained in-house. Operational sovereignty is the architectural commitment to zero external reliance.", accent: "purple" },
            { title: "Security Sovereignty", body: "The capacity to protect communications, data, and operations from interception, exposure, and compromise. Sovereign security architecture operates independently of foreign-controlled infrastructure.", accent: "emerald" },
            { title: "Strategic Sovereignty", body: "The capacity to set direction and execute strategy without external influence or constraint. Strategic sovereignty requires diagnostic capability, decisional authority, and the operational means to enforce decisions.", accent: "red" },
            { title: "Reputational Sovereignty", body: "The capacity to control how the entity is perceived across every digital surface and public conversation. Reputation is the asset that determines the strategic environment in which all other sovereign operations occur.", accent: "amber-gold" },
            { title: "Institutional Sovereignty", body: "The capacity to sustain sovereignty across leadership transitions, generational change, and operational disruptions. Institutional sovereignty is achieved when the architecture is structural rather than dependent on individuals.", accent: "denim" },
          ]}
          variant="card"
          columns={3}
        />
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Compass className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">Usage Examples</span>
          </div>
          <h2 className="text-display-md mb-4">Strategic sovereignty in practice.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Four operating contexts where Strategic Sovereignty is the operative standard — each example drawn from sovereign-scale operational practice.
          </p>
        </div>
        <FeatureGridBlock
          features={[
            { title: "National Election Operations", body: "An entire national campaign — intelligence gathering, voter outreach, content distribution, ground truth verification, and crisis response — operated without reliance on foreign platforms, third-party analytics, or external advisory. The full stack under sovereign control.", accent: "red" },
            { title: "Royal Family Engagements", body: "Multi-year advisory relationships for royal houses across three continents. Intelligence, perception, security, and governance services integrated under a single operational command. No external dependencies for any critical function.", accent: "purple" },
            { title: "Crisis Recovery Operations", body: "A Fortune-scale corporation under existential reputational threat. The crisis response engine activates 500+ pre-built playbooks within minutes. Recovery is managed entirely through the integrated operational architecture.", accent: "cyan" },
            { title: "Governance Transformation", body: "A national government transforming citizen service delivery through eGovernance deployment. The digital infrastructure, the implementation methodology, and the institutional handover are all operated under sovereign control.", accent: "emerald" },
          ]}
          columns={2}
        />
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <BookOpen className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">Common Misconceptions</span>
          </div>
          <h2 className="text-display-md mb-4">What Strategic Sovereignty is not.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Six persistent misconceptions that obscure what Strategic Sovereignty actually is. The misconceptions are common because the term has been adopted by organizations that do not practice the discipline.
          </p>
        </div>
        <AccordionBlock
          items={[
            { q: "Is Strategic Sovereignty the same as autarky?", a: "No. Strategic Sovereignty is not autarky. The discipline does not require self-sufficiency in every domain. It requires the capacity to operate without dependency in critical domains — intelligence, security, perception, political strategy, policy. Non-critical dependencies may remain." },
            { q: "Can Strategic Sovereignty be achieved through technology alone?", a: "No. Technology is necessary but insufficient. The architecture, the operational discipline, the human capability, and the institutional commitment must all be present. A sovereign technology stack operated by non-sovereign processes produces a fragile state, not strategic sovereignty." },
            { q: "Is Strategic Sovereignty a cost optimization strategy?", a: "No. Strategic Sovereignty is often more expensive than dependency-based approaches. The investment in proprietary platforms, in-house talent, and sovereign infrastructure is substantial. The return is measured in resilience, speed, and outcome control — not in cost reduction." },
            { q: "Does Strategic Sovereignty mean operating in isolation?", a: "No. Strategic Sovereignty means operating without dependency. Engagements with other entities — clients, partners, allies — continue. The discipline is about which functions are critical and which can be exposed. Critical functions are sovereign; non-critical functions may integrate." },
            { q: "Is Strategic Sovereignty only relevant for nation-states?", a: "No. Strategic Sovereignty applies to any entity whose operations are consequential — governments, corporations, royal families, defense agencies, political campaigns, and high-net-worth individuals. The scale and stakes vary. The discipline does not." },
            { q: "Can Strategic Sovereignty be achieved through outsourcing to a single vendor?", a: "No. Outsourcing critical functions to a single vendor creates a different form of dependency, not sovereignty. Strategic Sovereignty requires in-house capability for every critical function. Vendor relationships may supplement — never replace — the sovereign capability." },
          ]}
        />
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-red/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-purple/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <Crown className="size-10 text-red mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Engage sovereign-scale operations.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Strategic Sovereignty is not a service line. It is the operational state produced by integrated intelligence, sovereign infrastructure, and a clinical command method. Engage the operating architecture.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/contact/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Mic className="size-4" />
                Begin an Engagement
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/about/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <Crown className="size-4" />
                About Dr. Jyoti Kush
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "Glossary Hub", description: "Ten terms defining sovereign-scale operations.", href: "/glossary/", icon: BookOpen, accent: "cyan" },
          { title: "About Dr. Jyoti Kush", description: "The full story. Physician. Operator. Architect.", href: "/about/", icon: Crown, accent: "red" },
          { title: "FAQ Hub", description: "Eight subpages covering every domain of expertise.", href: "/faq/", icon: Sparkles, accent: "purple" },
          { title: "The Operator's Notebook", description: "Biweekly insights on how excellent things run.", href: "/the-operators-notebook/", icon: BookOpen, accent: "amber-gold" },
        ]}
      />
    </>
  );
}
